import { createSelector } from 'reselect'
import { DateTime } from 'luxon'
import orderBy from 'lodash/orderBy'

import { CalendlyLink } from '@/reducers/calendlyLink.slice'
import {
  UserCallEvent,
  UserCallStateOfEvent,
} from '@/reducers/finances/userCallEventsReducer'
import { ReduxState } from '../utils/typeHelpers'

export const getCalendlyLinks = (state: ReduxState) => state.calendlyLinks

// Admin
export const getAllUserCallEvents = (state: ReduxState) =>
  state.admin.allUserCallEvents

export const getAllUserCallEventsByUserId = createSelector(
  getAllUserCallEvents,
  (_: unknown, userId: number) => userId,
  (byUserId, userId) => (byUserId[userId] ? byUserId[userId] : undefined)
)

export const getAllUserCallEventById = createSelector(
  getAllUserCallEventsByUserId,
  (_: unknown, __: unknown, callEventId: number) => callEventId,
  (allUserCallEvents, callEventId) =>
    allUserCallEvents?.[callEventId]
      ? allUserCallEvents[callEventId]
      : undefined
)

export const getAdminCalendlyLinkByUserEventId = createSelector(
  getAllUserCallEventById,
  getCalendlyLinks,
  (callEvent, calendlyLinks) =>
    callEvent?.adminCalendlyLinkId &&
    calendlyLinks[callEvent.adminCalendlyLinkId]
      ? calendlyLinks[callEvent.adminCalendlyLinkId]
      : undefined
)

export const getCurrentYearAllUserCallEventIds = createSelector(
  getAllUserCallEventsByUserId,
  (callEvents) =>
    callEvents
      ? Object.values(callEvents)
          .filter((callEvent) => {
            return callEvent.year === DateTime.now().year.toString()
          })
          .map((callEvent) => callEvent.id)
      : []
)

export const getPastAllUserCallEventsIds = createSelector(
  getAllUserCallEventsByUserId,
  (callEvents) =>
    callEvents
      ? Object.values(callEvents)
          .filter(
            (callEvent) => callEvent.year !== DateTime.now().year.toString()
          )
          .map((callEvent) => callEvent.id)
      : []
)

export const getAllUserCallEventsSorted = createSelector(
  getAllUserCallEventsByUserId,
  (callEvents) => callEvents && orderBy(callEvents, ['startTime'], ['desc'])
)

export const getCalendlyLinkIds = createSelector(
  getCalendlyLinks,
  (calendlyLinks) => Object.values(calendlyLinks).map((link) => link.id)
)

export const getCalendlyLinkById = createSelector(
  getCalendlyLinks,
  (_: unknown, id: number | undefined) => id,
  (calendlyLinks, id) =>
    id && calendlyLinks[id] ? calendlyLinks[id] : undefined
)

export const getOffsetYear = () => {
  const now = DateTime.now()
  const offsetYearDate = now.set({ month: 1, day: 7 }).startOf('day')
  return now > offsetYearDate ? now.year : now.year - 1
}

export const getUserCallLabelColorClass = (status: UserCallStateOfEvent) => {
  switch (status) {
    case 'missed':
      return 'red'
    case 'skipped':
      return 'orange'
    case 'upcoming':
    case 'unscheduled':
      return 'green'
    case 'not_required':
    case 'completed':
      return 'neutral'
    default:
      return 'dark'
  }
}

export const getCallEventName = (
  callEvent: UserCallEvent,
  calendlyLink: CalendlyLink | undefined
) => {
  if (calendlyLink?.userFacingEventName) {
    return calendlyLink?.userFacingEventName
  } else if (callEvent.callName) {
    return callEvent.callName
  } else if (callEvent.quarter) {
    return `Q${callEvent.quarter}`
  }

  return ''
}

export const QUARTER_DROPDOWN_OPTIONS = [
  {
    text: 'Q1',
    value: '1',
  },
  {
    text: 'Q2',
    value: '2',
  },
  {
    text: 'Q3',
    value: '3',
  },
  {
    text: 'Q4',
    value: '4',
  },
]

export const createYearDropdownValues = () => {
  const currentYear = DateTime.now().year

  const options = []

  for (let i = currentYear - 1; i < currentYear + 3; i++) {
    options.push({ text: i.toString(), value: i.toString() })
  }

  return options
}
